import { Breadcrumb } from '@/components/Breadcrumb';
import { Container } from '@/components/Container';
import { ListingFilters } from '@/components/ListingFilters';
import type { SectionHeaderProps } from '@/components/SectionHeader';
import { SectionHeader } from '@/components/SectionHeader';
import { useHasActiveFilters } from '@/integrations/algolia/helpers';
import { getSubheadingType } from '@/lib/title';
import { theme } from '@/theme';

import type { HeroProductListingProps } from '../../types';
import { useGenerateBreadCrumbItems } from './helpers';

import * as S from './ListingType.styles';

export const ListingType = ({
  backgroundColor,
  backgroundColorOpacity,
  heading,
  headingNumberOfColoredWords,
  heroProductListingTitle: title,
  media,
  pageName,
  showBreadcrumbs,
  showFilters,
  subheader,
  subheaderIsCapitalized = true,
  textAlignment,
  textBelowMedia = false,
  type,
  visibleInDesktop = true,
  visibleInMobile = true,
}: HeroProductListingProps) => {
  const breadCrumbItems = useGenerateBreadCrumbItems({
    heading,
    type,
    pageName,
  });

  const headingType = (title?.headingSeoTag ??
    'h3') as keyof HTMLElementTagNameMap;
  const headingText = title?.text ?? heading;
  const hasActiveFilters = useHasActiveFilters();

  // Text content to be rendered either over or below the media
  const renderTextContent = (isBelowMedia = false) => (
    <S.Heading
      style={
        isBelowMedia
          ? {
              position: 'static',
              transform: 'none',
              margin: '0.5em 0',
            }
          : {}
      }
    >
      {headingText && (
        <SectionHeader
          color={theme.colors.black}
          data-test-id='ListingTypeSectionHeader'
          disableGutters
          htmlElementTag={headingType}
          numberOfColoredWords={headingNumberOfColoredWords}
          text={headingText}
          textAlign={textAlignment as SectionHeaderProps['textAlign']}
        />
      )}
      {subheader && subheaderIsCapitalized && (
        <S.SubheadingCapitalized
          as={getSubheadingType(headingType)}
          data-test-id='ListingTypeSubheading'
        >
          {subheader}
        </S.SubheadingCapitalized>
      )}
      {subheader && !subheaderIsCapitalized && (
        <S.SubheadingLowercase
          as={getSubheadingType(headingType)}
          data-test-id='ListingTypeSubheading'
        >
          {subheader}
        </S.SubheadingLowercase>
      )}
    </S.Heading>
  );

  return (
    <>
      <Container
        data-test-id='ListingTypeContainer1'
        disableGutters
        maxWidth='xl'
      >
        <Breadcrumb.Wrapper data-test-id='ListingTypeContainer2'>
          {showBreadcrumbs && (
            <Breadcrumb
              data-test-id='ListingTypeBreadcrumb'
              items={breadCrumbItems}
            />
          )}
        </Breadcrumb.Wrapper>

        {!textBelowMedia ? (
          // Original layout with text over image
          <S.Header
            $backgroundColorOpacity={backgroundColorOpacity}
            $noBgImage={!media?.image?.url}
            backgroundColor={backgroundColor?.value}
            backgroundImage={media?.image?.url}
          >
            <Container>{renderTextContent(false)}</Container>
          </S.Header>
        ) : (
          // New layout with image and text below it
          <>
            <S.MediaContainer
              $noBgImage={!media?.image?.url}
              backgroundImage={media?.image?.url}
            />
            <S.TextBelowContainer
              backgroundColor={backgroundColor?.value}
              backgroundColorOpacity={backgroundColorOpacity}
            >
              <Container>{renderTextContent(true)}</Container>
            </S.TextBelowContainer>
          </>
        )}
      </Container>

      {!!showFilters && (
        <S.FiltersVisibilityWrapper
          $visibleInDesktop={visibleInDesktop}
          $visibleInMobile={visibleInMobile}
        >
          <ListingFilters
            collectionData={[]}
            data-test-id='ListingTypeListingFilters'
            forceMount={hasActiveFilters}
            showCollectionFilter={pageName === 'all'}
            showMaterialsFilter={pageName === 'all'}
          />
        </S.FiltersVisibilityWrapper>
      )}
    </>
  );
};
